import { useState, type FormEvent } from 'react';
import { useRemindInvoice } from '@repo/queries';
import type { Invoice, InvoiceReminderTone } from '@repo/api-client/dtos';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';

interface RemindInvoiceDialogProps {
  invoice: Invoice;
  defaultRecipient?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TONES: { value: InvoiceReminderTone; label: string; description: string }[] = [
  { value: 'friendly', label: 'Friendly', description: 'A gentle nudge for an early reminder.' },
  { value: 'firm', label: 'Firm', description: 'Direct and matter-of-fact.' },
  { value: 'final', label: 'Final', description: 'For overdue invoices that need urgent attention.' },
];

export function RemindInvoiceDialog({
  invoice,
  defaultRecipient,
  open,
  onOpenChange,
}: RemindInvoiceDialogProps) {
  const { sendReminder, loading } = useRemindInvoice();
  const { toast } = useToast();
  const [tone, setTone] = useState<InvoiceReminderTone>('friendly');
  const [message, setMessage] = useState('');
  const [recipient, setRecipient] = useState(defaultRecipient ?? '');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      await sendReminder({
        invoiceId: invoice.id,
        tone,
        ...(message ? { message } : {}),
        ...(recipient ? { recipient } : {}),
      });
      toast({
        title: 'Reminder sent',
        description: `${tone} reminder delivered.`,
        variant: 'success',
      });
      onOpenChange(false);
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send reminder for {invoice.invoiceNumber}</DialogTitle>
          <DialogDescription>
            Reminders don't change invoice status. Pick a tone, optionally add a note,
            and we'll email the client.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Field label="Tone" htmlFor="tone">
            <Select value={tone} onValueChange={(v) => setTone(v as InvoiceReminderTone)}>
              <SelectTrigger id="tone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TONES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              {TONES.find((t) => t.value === tone)?.description}
            </p>
          </Field>
          <Field label="Recipient" htmlFor="recipient" hint="Leave blank to use the email on file.">
            <Input
              id="recipient"
              type="email"
              placeholder={defaultRecipient ?? 'client@example.com'}
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
            />
          </Field>
          <Field label="Personal note" htmlFor="message">
            <Textarea
              id="message"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Optional. Appears just under the kicker."
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Send reminder
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
